import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

/**
 * Lightweight hook that checks and toggles the favorite status of a single arche.
 * Used by FavoriteButton in arche headers and marketplace.
 */
export function useFavoriteStatus(archId: string) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const result = await api.checkFavorite(archId);
        if (!cancelled) {
          setIsFavorite(result.isFavorite);
        }
      } catch (err) {
        console.error('Failed to check favorite status:', err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    check();

    return () => {
      cancelled = true;
    };
  }, [archId]);

  const toggle = useCallback(async () => {
    // Optimistic update
    const prev = isFavorite;
    setIsFavorite(!prev);

    try {
      if (prev) {
        await api.removeFavorite(archId);
      } else {
        await api.addFavorite(archId);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      // Revert on error
      setIsFavorite(prev);
    }
  }, [archId, isFavorite]);

  return { isFavorite, isLoading, toggle };
}
